// walkTrack — shared cleaning for scout GPS walk tracks.
//
// A raw walk track is the array of GeolocationPosition fixes captured during a
// scout's walk. Two real-world artifacts make the raw polyline lie about where
// the scout actually went (see the 18 Jun 2026 incident — a "5.47 km" walk
// whose median fix was a clean 4.9 m but which drew long straight lines through
// town):
//
//   1. Coarse fixes — the phone falls back to cell-tower / wifi location
//      (accuracy hundreds-to-thousands of metres; one fix reported ±3,754 m)
//      and reports a point far from the true path. The next real GPS fix snaps
//      back, drawing a spike out and back.
//
//   2. Signal gaps — the device stops reporting for minutes (pocketed, app
//      backgrounded, under cover). The scout keeps walking; when fixes resume,
//      a single LineString bridges the gap with one straight line across ground
//      that was never walked in a straight line.
//
// cleanWalkTrack() drops coarse fixes (1) and splits the remaining points into
// segments at gaps / teleports (2). Drawing each segment as its own line means
// a gap renders as an honest break, not a phantom straight line — and the
// recomputed distance excludes the bridges that inflated the stored total.

export interface WalkPoint {
  lat: number
  lng: number
  t:   number   // epoch ms
  acc: number   // accuracy, metres (GeolocationCoordinates.accuracy)
}

// A fix worse than this is a network-location guess, not GPS. Orchard walks see
// single-digit-metre fixes (median ~5 m on real walks), so this keeps every
// legitimate point and drops the cell-tower fallbacks that draw spikes. Used at
// BOTH capture time (useGeolocation) and render time (so already-saved walks,
// which predate the capture gate, are cleaned on display too).
export const WALK_MAX_ACCURACY_M = 35

// Break the line when the device went quiet longer than this. Past it, a
// straight bridge between fixes is a guess, not a path — better shown as a gap.
export const WALK_GAP_MS = 30_000

// Break the line on an implausible single-step jump (a spike whose accuracy
// slipped under the gate). 120 m between consecutive on-foot fixes at the
// watcher's ~1 s cadence is physically impossible, so it is always an artifact.
export const WALK_MAX_JUMP_M = 120

// Haversine — metres between two lat/lng (WGS84).
export function haversineMeters(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6_371_000
  const toRad = (d: number) => d * Math.PI / 180
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const lat1 = toRad(a.lat)
  const lat2 = toRad(b.lat)
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(h))
}

export interface CleanedWalk {
  segments:  WalkPoint[][]   // continuous runs; draw each as its own line (render the ≥2-pt ones)
  points:    WalkPoint[]     // all kept points, flattened (for markers / replay scrubber)
  distanceM: number          // within-segment distance — excludes gap bridges and spikes
  dropped:   number          // points removed by the accuracy gate
}

export function cleanWalkTrack(
  track: readonly WalkPoint[] | null | undefined,
  opts?: { maxAccuracyM?: number; gapMs?: number; maxJumpM?: number },
): CleanedWalk {
  const maxAcc  = opts?.maxAccuracyM ?? WALK_MAX_ACCURACY_M
  const gapMs   = opts?.gapMs ?? WALK_GAP_MS
  const maxJump = opts?.maxJumpM ?? WALK_MAX_JUMP_M

  const raw = track ?? []
  // Keep only trustworthy fixes. Guard against malformed/legacy rows: require
  // finite coords; a missing/NaN acc is treated as acceptable (some legacy
  // points predate the acc field) rather than silently dropped.
  const kept = raw.filter(p =>
    p != null &&
    Number.isFinite(p.lat) && Number.isFinite(p.lng) &&
    (!Number.isFinite(p.acc) || p.acc <= maxAcc))
  const dropped = raw.length - kept.length

  const segments: WalkPoint[][] = []
  let cur: WalkPoint[] = []
  let distanceM = 0
  for (const p of kept) {
    const prev = cur[cur.length - 1]
    if (prev) {
      const dt = p.t - prev.t
      const d  = haversineMeters(prev, p)
      // A quiet stretch (gap) or a teleport (jump) ends the current segment —
      // we neither draw nor count the bridge across it.
      if (dt > gapMs || d > maxJump) {
        segments.push(cur)
        cur = [p]
        continue
      }
      distanceM += d
    }
    cur.push(p)
  }
  if (cur.length) segments.push(cur)

  return { segments, points: segments.flat(), distanceM, dropped }
}
